import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";
import { ThemeProvider, useColorScheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Typography } from "@mui/material";
import { createBrandTheme, Logo, colors } from "brand";
import {
  AccountMenu,
  AppBar,
  AppFooter,
  AppLayout,
  CognitoAuthProvider,
  ProtectedRoute,
  CallbackPage,
  LogoutPage,
  LoggedOutPage,
  ActiveOrgProvider,
  ActiveShowProvider,
  OrgSwitcher,
  ShowSwitcher,
  useActiveOrg,
  UserDisplayName,
  BuildBadge,
  UsaBadge,
} from "common-fe";
import { OrganizationApi, Configuration } from "account-common";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { useScoreApi, unwrap } from "./api/scoreApi";
import { AnnouncerProvider } from "./context/AnnouncerContext";
import AnnouncerRoleGate from "./components/AnnouncerRoleGate";
import OrderOfGo from "./components/OrderOfGo";
import { handleLogout } from "./utils/logout";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Inject the org-list fetcher (built from the generated account-common client)
// into the shared ActiveOrgProvider so announcer-fe sends the active-org
// X-Org-Id header on every score-api call — same pattern as scribe-fe /
// scoreadmin-fe. Without this the API falls back to the token's first org and
// an announcer with several org memberships would see the wrong show list.
const AnnouncerActiveOrgProvider = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const listOrgs = useCallback(
    async (isSuper) => {
      if (!accessToken) return [];
      const api = new OrganizationApi(
        new Configuration({ basePath: ACCOUNT_API_BASE_URL, accessToken })
      );
      const res = isSuper
        ? await api.listAllOrganizations(true) // enabledOnly (#2074): switcher shows only superadminEnabled orgs
        : await api.listOrganizations();
      return res.data?.status === "success" && Array.isArray(res.data.data)
        ? res.data.data
        : [];
    },
    [accessToken]
  );
  return <ActiveOrgProvider listOrgs={listOrgs}>{children}</ActiveOrgProvider>;
};

// #2112 — shared active-show, org-scoped, the same pattern scoreadmin-fe's
// ScoreadminActiveShowProvider uses. listShows is the generated score client
// (already X-Org-Id scoped); reloads when the active org changes. Replaces
// the old kiosk InitialSetup screen: the show is now a free switch from the
// app bar's ShowSwitcher, not a one-time, PIN-gated pick.
//
// #2190 — no local caching: a listShows() failure surfaces to
// ActiveShowProvider's own catch, same as any other consumer of that shared
// provider. NOTE this fetch is NOT one-shot-per-org-change in practice:
// ActiveShowProvider's effect deps are [listShows, orgId], and this
// provider's listShows memoises down to [accessToken, activeOrgId], so it
// re-runs on every access-token renew too, not only an org switch. A
// renew-time failure used to blank shows/activeShow mid-session with no
// self-heal until the next renew (#2215); ActiveShowProvider now keeps the
// last-good `shows` for the same org across a failed reload instead of
// resetting to [], so a transient renew-time failure leaves the running
// order on screen (stale, not blank) until the next successful reload.
const AnnouncerActiveShowProvider = ({ children }) => {
  const scoreApi = useScoreApi();
  const { activeOrgId } = useActiveOrg();
  const listShows = useCallback(async () => {
    return unwrap(await scoreApi.shows.listShows());
  }, [scoreApi]);
  return (
    // #2222 — announcer opts into the date-window filter (AC4): live shows,
    // not every show the org has ever ingested.
    <ActiveShowProvider listShows={listShows} orgId={activeOrgId} filterActive>
      {children}
    </ActiveShowProvider>
  );
};

function BrandLogoMark() {
  const { t } = useTranslation("announcer");
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Logo
        style={{
          paddingLeft: 8,
          height: 40,
          width: "auto",
          marginRight: 16,
          flexShrink: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.title")}
      </Typography>
    </Box>
  );
}

// #2112 — the normal app shell (AppLayout + AppBar), matching scoreadmin-fe's
// AdminShell. No NavRail: announcer has exactly one screen (Order of Go), so
// there is nothing to navigate between — AppLayout's `navRail` prop is
// optional and simply omitted here.
function AnnouncerShell({ children }) {
  const { t } = useTranslation("announcer");
  const { mode, setMode } = useColorScheme();

  const handleThemeToggle = () => {
    setMode(mode === "dark" ? "light" : "dark");
  };

  const accountMenuItems = [
    {
      icon: <SettingsIcon />,
      text: t("accountMenu.accountSettings"),
      onClick: () => {
        window.location.href = import.meta.env.VITE_ACCOUNT_FE_BASE_URL + "/settings";
      },
    },
    {
      icon: mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />,
      text: mode === "dark" ? t("accountMenu.lightMode") : t("accountMenu.darkMode"),
      onClick: handleThemeToggle,
    },
    {
      icon: <LogoutIcon />,
      text: t("accountMenu.signOut"),
      onClick: handleLogout,
    },
  ];

  const appBarRightContent = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <OrgSwitcher />
      <ShowSwitcher />
      <UserDisplayName />
      <AccountMenu menuItems={accountMenuItems} />
    </Box>
  );

  return (
    <AppLayout
      appBar={
        <AppBar
          sx={{ backgroundColor: colors.darkGrey, color: "#fff" }}
          rightContent={appBarRightContent}
          brandLogo={<BrandLogoMark />}
        />
      }
      appFooterEnabled={true}
      appFooter={
        <AppFooter sx={{ backgroundColor: colors.darkGrey, color: "#fff" }}>
          <Typography variant="body2">
            © {new Date().getFullYear()} Fox Village.
          </Typography>
        </AppFooter>
      }
    >
      {children}
    </AppLayout>
  );
}

function AppContent({ cognitoAuthConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  useColorScheme();

  return (
    <CognitoAuthProvider cognitoAuthConfig={cognitoAuthConfig} location={location}>
      <AnnouncerActiveOrgProvider>
        <Routes>
          <Route
            path="/callback"
            element={<CallbackPage navigateFunction={navigate} location={location} />}
          />
          <Route path="/logout" element={<LogoutPage navigateFunction={navigate} />} />
          <Route path="/logged-out" element={<LoggedOutPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AnnouncerRoleGate>
                  <AnnouncerActiveShowProvider>
                    <AnnouncerProvider>
                      <AnnouncerShell>
                        <Routes>
                          <Route path="/" element={<Navigate to="/oog" replace />} />
                          {/* #642 replaces this placeholder with the real Order of Go. */}
                          <Route path="/oog" element={<OrderOfGo />} />
                          <Route path="*" element={<Navigate to="/oog" replace />} />
                        </Routes>
                      </AnnouncerShell>
                    </AnnouncerProvider>
                  </AnnouncerActiveShowProvider>
                </AnnouncerRoleGate>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AnnouncerActiveOrgProvider>
    </CognitoAuthProvider>
  );
}

export default function App({ cognitoAuthConfig }) {
  const theme = createBrandTheme();
  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <AppContent cognitoAuthConfig={cognitoAuthConfig} />
      <UsaBadge />
      <BuildBadge />
    </ThemeProvider>
  );
}
